import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CloseIcon from '@mui/icons-material/Close';
import '../css/Dialog.css';

const LeaveActionDialog = ({
  open,
  status,
  row,
  comment,
  onCommentChange,
  onConfirm,
  onClose,
  fmtDate,
}) => {
  const [touched, setTouched] = React.useState(false);
  const isApprove = status === 'APPROVED';
  const isCancellation = row?.status === 'CANCELLATION_REQUESTED';

  React.useEffect(() => {
    if (open) {
      setTouched(false);
    }
  }, [open]);

  const isCommentEmpty = !comment || !comment.trim();
  const showError = touched && isCommentEmpty;

  const handleConfirmClick = () => {
    setTouched(true);
    if (isCommentEmpty) return;
    onConfirm();
  };

  let dialogTitle = isApprove ? 'Approve Leave Request' : 'Reject Leave Request';
  if (isCancellation) {
    dialogTitle = isApprove ? 'Approve Cancellation Request' : 'Reject Cancellation Request';
  }

  let descriptionText = '';
  if (isCancellation) {
    descriptionText = isApprove
      ? `Approve leave cancellation for ${row?.userName ?? 'this applicant'}? The leave status will become Cancelled.`
      : `Reject leave cancellation for ${row?.userName ?? 'this applicant'}? The leave will remain Approved.`;
  } else {
    descriptionText = isApprove
      ? `Approve leave request from ${row?.userName ?? 'this applicant'}? This cannot be undone.`
      : `Reject leave request from ${row?.userName ?? 'this applicant'}? This cannot be undone.`;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{ paper: { className: 'modern-dialog' } }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle
        className={`modern-dialog-title${isApprove ? '' : ' delete-header'}`}
      >
        <span className="modern-dialog-title-text">
          {dialogTitle}
        </span>
        <IconButton aria-label="close" onClick={onClose} className="modern-dialog-close-btn">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="modern-dialog-content">
        {row && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {descriptionText}
            </Typography>

            {/* Date · Reason summary */}
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', mb: 1, fontWeight: 600 }}
            >
              {fmtDate(row.startDate)} – {fmtDate(row.endDate)}
              {row.reason ? `  ·  ${row.reason}` : ''}
            </Typography>

            {isCancellation && row.cancellationReason && (
              <Typography
                variant="caption"
                color="warning.main"
                sx={{ display: 'block', mb: 1.5, fontWeight: 600 }}
              >
                Cancellation Reason: {row.cancellationReason}
              </Typography>
            )}

            <Divider sx={{ mb: 2 }} />

            {/* Comment textarea */}
            <TextField
              id="leave-action-comment"
              label="Comment"
              required
              multiline
              rows={3}
              fullWidth
              size="small"
              value={comment}
              onChange={(e) => {
                setTouched(true);
                onCommentChange(e.target.value);
              }}
              onBlur={() => setTouched(true)}
              error={showError}
              helperText={showError ? 'Comment is required' : ''}
              placeholder="Add a reason or note for the applicant…"
            />
          </>
        )}
      </DialogContent>

      <DialogActions className="modern-dialog-actions">
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          sx={{ borderColor: '#e2e8f0', color: '#64748b' }}
        >
          Cancel
        </Button>
        <Button
          id={`leave-confirm-${status?.toLowerCase()}-btn`}
          onClick={handleConfirmClick}
          variant="contained"
          color={isApprove ? 'success' : 'error'}
          disableElevation
          disabled={isCommentEmpty}
          startIcon={
            isApprove
              ? <CheckCircleOutlineIcon fontSize="small" />
              : <CancelOutlinedIcon fontSize="small" />
          }
        >
          {isApprove ? 'Yes, Approve' : 'Yes, Reject'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeaveActionDialog;
